import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { ArrowLeft, ArrowRight, Check, ShieldCheck } from "lucide-react";
import type { AssessmentInput } from "../lib/types";

type Draft = Omit<AssessmentInput, "favoriteSubjects" | "weakSubjects" | "skills" | "interests" | "hobbies" | "workStyle" | "consentAccepted"> & {
  favoriteSubjects: string; weakSubjects: string; skills: string; interests: string; hobbies: string; workStyle: string; consentAccepted: boolean;
};

const initial: Draft = {
  name: "", ageRange: "19_22",
  educationLevel: "", streamOrDegree: "", institutionType: "",
  favoriteSubjects: "", weakSubjects: "", skills: "", interests: "", hobbies: "",
  workStyle: "", preferredPath: "not_sure", incomeGoal: "", locationPreference: "",
  learningTimePerWeek: "", courseBudget: "", constraints: "", rawNotes: "", consentAccepted: false
};

const steps = ["About you", "Education", "Interests", "Skills", "Work style", "Goals"];
const requiredByStep: Array<Array<keyof Draft>> = [
  ["name", "ageRange"], ["educationLevel", "streamOrDegree", "favoriteSubjects"],
  ["interests"], ["skills"], ["workStyle", "locationPreference"], ["learningTimePerWeek", "preferredPath", "consentAccepted"]
];

const split = (value: string) => value.split(",").map((item) => item.trim()).filter(Boolean);

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return <label className="block"><span className="field-label">{label}</span>{children}{hint && <span className="mt-2 block text-xs leading-5 text-ink/45">{hint}</span>}</label>;
}

export function AssessmentForm({ onSubmit, submitting }: { onSubmit: (input: AssessmentInput) => Promise<void>; submitting: boolean }) {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<Draft>(initial);
  const [error, setError] = useState("");
  const progress = ((step + 1) / steps.length) * 100;
  const set = (key: keyof Draft, value: string | boolean) => setDraft((current) => ({ ...current, [key]: value }));
  const input = (key: keyof Draft, placeholder: string, type = "text") => (
    <input className="field" type={type} value={String(draft[key] ?? "")} placeholder={placeholder} onChange={(event) => set(key, event.target.value)} />
  );
  const canContinue = useMemo(() => requiredByStep[step]!.every((key) => Boolean(draft[key])), [draft, step]);

  const next = () => {
    if (!canContinue) return setError("Please complete the required fields before continuing.");
    setError("");
    setStep((value) => Math.min(steps.length - 1, value + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canContinue || !draft.consentAccepted) return setError("Please complete the required fields and accept the consent statement.");
    setError("");
    await onSubmit({
      ...draft,
      favoriteSubjects: split(draft.favoriteSubjects),
      weakSubjects: split(draft.weakSubjects),
      skills: split(draft.skills),
      interests: split(draft.interests),
      hobbies: split(draft.hobbies),
      workStyle: split(draft.workStyle),
      consentAccepted: true
    });
  };

  return (
    <form className="overflow-hidden rounded-[2rem] border border-moss/10 bg-white shadow-soft" onSubmit={submit}>
      <div className="h-1.5 bg-moss/10"><div className="h-full bg-coral transition-all duration-500" style={{ width: `${progress}%` }} /></div>
      <div className="border-b border-moss/10 px-6 py-5 md:flex md:items-center md:justify-between md:px-10">
        <p className="text-xs font-extrabold uppercase tracking-[.2em] text-moss/55">Step {step + 1} of {steps.length}</p>
        <div className="mt-3 flex flex-wrap gap-2 md:mt-0">
          {steps.map((label, index) => <span className={`h-2 rounded-full transition-all ${index === step ? "w-8 bg-moss" : index < step ? "w-2 bg-coral" : "w-2 bg-moss/15"}`} title={label} key={label} />)}
        </div>
      </div>
      <div className="p-6 md:p-10">
        <p className="eyebrow">{steps[step]}</p>
        <h2 className="font-display text-4xl leading-tight md:text-5xl">
          {[
            "Let’s start with the person, not a job title.",
            "What have you learned—and what feels natural?",
            "What pulls your curiosity forward?",
            "What can you already do with confidence?",
            "Where and how do you do your best work?",
            "Make the advice fit your real life."
          ][step]}
        </h2>

        <div className="mt-9 grid gap-6 md:grid-cols-2">
          {step === 0 && <>
            <Field label="Your name *">{input("name", "Aarav Sharma")}</Field>
            <Field label="Age range *"><select className="field" value={draft.ageRange} onChange={(event) => set("ageRange", event.target.value)}>
              <option value="under_16">Under 16</option><option value="16_18">16–18</option><option value="19_22">19–22</option><option value="23_30">23–30</option><option value="30_plus">30+</option>
            </select></Field>
            <Field label="Anything else we should know?" hint="Optional. Never share sensitive personal information."><textarea className="field min-h-28" value={draft.rawNotes} onChange={(event) => set("rawNotes", event.target.value)} placeholder="I am exploring options after graduation…" /></Field>
          </>}
          {step === 1 && <>
            <Field label="Education level *">{input("educationLevel", "B.Com second year")}</Field>
            <Field label="Stream or degree *">{input("streamOrDegree", "Commerce")}</Field>
            <Field label="Institution type">{input("institutionType", "College, school, distance learning…")}</Field>
            <Field label="Favourite subjects *" hint="Separate items with commas.">{input("favoriteSubjects", "Business Studies, Economics")}</Field>
            <Field label="Subjects you find difficult" hint="This helps us suggest realistic learning bridges.">{input("weakSubjects", "Advanced Mathematics")}</Field>
          </>}
          {step === 2 && <>
            <Field label="Interests *" hint="Use commas: business, technology, communication">{input("interests", "business, technology, communication")}</Field>
            <Field label="Hobbies" hint="Informal activities count too.">{input("hobbies", "organising events, startup videos")}</Field>
          </>}
          {step === 3 && <>
            <Field label="Current skills *" hint="Include classroom, practical, creative, and people skills.">{input("skills", "Excel, public speaking, basic accounting")}</Field>
            <div className="rounded-2xl bg-cream p-5 text-sm leading-6 text-ink/60"><b className="text-ink">Be accurate, not modest.</b><br />Beginner skills are useful signals. We never invent marks, experience, or achievements.</div>
          </>}
          {step === 4 && <>
            <Field label="Preferred work styles *" hint="Examples: teamwork, structured tasks, client-facing, independent">{input("workStyle", "teamwork, structured tasks, client-facing")}</Field>
            <Field label="City, state, or location preference *" hint="Required for verified nearby institute options.">{input("locationPreference", "Bengaluru, Karnataka")}</Field>
          </>}
          {step === 5 && <>
            <Field label="Preferred path *"><select className="field" value={draft.preferredPath} onChange={(event) => set("preferredPath", event.target.value)}>
              <option value="not_sure">Not sure yet</option><option value="job">Job</option><option value="freelance">Freelance</option><option value="business">Business</option><option value="higher_studies">Higher studies</option><option value="government_exam">Government exam</option>
            </select></Field>
            <Field label="Learning time each week *">{input("learningTimePerWeek", "8–10 hours")}</Field>
            <Field label="Income goal">{input("incomeGoal", "A stable role after graduation")}</Field>
            <Field label="Course budget">{input("courseBudget", "Low budget; free resources preferred")}</Field>
            <Field label="Constraints" hint="Family expectations, confidence gaps, accessibility, schedule, or anything practical."><textarea className="field min-h-28" value={draft.constraints} onChange={(event) => set("constraints", event.target.value)} placeholder="Not confident in coding yet…" /></Field>
            <label className="md:col-span-2 flex cursor-pointer items-start gap-3 rounded-2xl border border-moss/15 bg-cream p-5">
              <input className="mt-1 h-5 w-5 accent-moss" type="checkbox" checked={draft.consentAccepted} onChange={(event) => set("consentAccepted", event.target.checked)} />
              <span className="text-sm leading-6 text-ink/65"><b className="text-ink">I consent to this assessment being processed.</b><br />I understand this is AI-assisted exploration, not a guarantee or a clinical assessment.</span>
            </label>
            <div className="md:col-span-2 flex items-center gap-2 text-xs font-semibold text-moss"><ShieldCheck size={16} /> Your report is generated server-side; private keys never enter the browser.</div>
          </>}
        </div>
        {error && <p className="mt-5 rounded-xl bg-coral/15 px-4 py-3 text-sm font-semibold text-ink">{error}</p>}
        <div className="mt-10 flex items-center justify-between">
          <button className="btn-ghost" type="button" disabled={step === 0} onClick={() => setStep((value) => Math.max(0, value - 1))}><ArrowLeft size={17} /> Back</button>
          {step < steps.length - 1
            ? <button className="btn-dark" type="button" onClick={next}>Continue <ArrowRight size={17} /></button>
            : <button className="btn-dark" disabled={submitting} type="submit">{submitting ? "Starting agents…" : "Build my free preview"} <Check size={17} /></button>}
        </div>
      </div>
    </form>
  );
}
